import {Chess} from 'chess.js';

// DEVIATION from the plan: relative import instead of the '@' alias. This
// import is type-only (CourseChapter/CourseMove/CoursePage/VideoSegment are
// only ever used in type positions in this file), so babel would likely elide
// the runtime require anyway — but jest still has no moduleNameMapper for '@'
// (see useGeneratedCourse.ts), and jest.config.js may not be touched, so a
// relative path is used to remove any doubt.
import {CourseChapter, CourseMove, CoursePage, VideoSegment} from '../../Hooks/LmsCourseApi/types';

export type MoveOutcome =
  | {status: 'wrong'}
  | {
      status: 'reply';
      fen: string;
      /**
       * The position after the student's move but before the reply. The UI
       * shows this first and lands the reply a beat later, so the opponent
       * does not appear to move at the same instant as the student.
       */
      afterStudentFen: string;
      replyMove: string;
      studentSan: string;
      replySan: string;
      nextPly: number;
    }
  | {status: 'solved'; fen: string; studentSan: string};

export function isVideoPage(page: CoursePage) {
  return page?.content_type === 'video';
}

export function isPuzzlePage(page: CoursePage) {
  return page?.content_type === 'chess_position';
}

/**
 * A generated course points every video chapter at the same recording,
 * distinguished by this window. A page without one is not playable as a slice.
 */
export function getVideoSegment(page: CoursePage): VideoSegment | null {
  const raw = page?.config?.video_segment as {start: unknown; end: unknown} | undefined;
  if (!raw) return null;
  const start = Number(raw.start);
  const end = Number(raw.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return {start, end};
}

/**
 * Every position along a line, starting with the one you are given.
 *
 * Used to step through a tutor answer on the board. Returns `n + 1` positions
 * for `n` legal moves, and stops at the first move that will not play — though
 * the server has already validated these, so that is a belt-and-braces guard
 * rather than an expected path.
 */
export function positionsAlongLine(fen: string, line: string[]): string[] {
  let game: InstanceType<typeof Chess>;
  try {
    game = new Chess(fen);
  } catch {
    return [];
  }

  const positions = [game.fen()];
  for (const move of line ?? []) {
    const uci = parseUci(move ?? '');
    if (!uci) break;
    try {
      const played = game.move({
        from: uci.from,
        to: uci.to,
        promotion: uci.promotion ?? 'q',
      });
      if (!played) break;
    } catch {
      break;
    }
    positions.push(game.fen());
  }
  return positions;
}

/**
 * Squares the piece on `square` may legally move to.
 *
 * Click-to-move is implemented here rather than left to chessboard-package:
 * its own click handling keeps the selection in props supplied by its wrapper,
 * and in this app that state never updates — clicking a piece highlights
 * nothing, so the board reads as dead even though the move itself lands.
 */
export function legalDestinations(fen: string, square: string): string[] {
  if (!/^[a-h][1-8]$/.test(square || '')) return [];
  try {
    const game = new Chess(fen);
    return game
      .moves({square: square as never, verbose: true})
      .map((m: {to: string}) => m.to);
  } catch {
    return [];
  }
}

/**
 * The square the solving piece starts on, from the scripted first move.
 *
 * Used as the "show me where" hint when the course was generated before hints
 * were stored on the page, so older courses still get one.
 */
export function firstMoveSquare(moves: CourseMove[] | null): string {
  const first = expectedMoveForPly(moves, 1);
  const uci = (first?.move ?? '').trim().toLowerCase();
  return /^[a-h][1-8]/.test(uci) ? uci.slice(0, 2) : '';
}

export function expectedMoveForPly(
  moves: CourseMove[] | null,
  ply: number,
): CourseMove | undefined {
  return (moves ?? []).find(m => m.ply === ply);
}

function parseUci(uci: string) {
  const match = uci.trim().toLowerCase().match(/^([a-h][1-8])([a-h][1-8])([nbrq])?$/);
  if (!match) return null;
  return {from: match[1], to: match[2], promotion: match[3]};
}

/**
 * The `single_variant` contract: the student plays odd plies, the engine replies
 * on even plies, and the line ends on an odd ply. A move that is not the
 * scripted one is rejected without mutating the position.
 */
export function applyStudentMove(
  fen: string,
  moves: CourseMove[] | null,
  ply: number,
  from: string,
  to: string,
  promotion?: string,
): MoveOutcome {
  const scripted = expectedMoveForPly(moves, ply);
  if (!scripted) return {status: 'wrong'};

  const expected = parseUci(scripted.move);
  if (!expected) return {status: 'wrong'};
  if (expected.from !== from || expected.to !== to) return {status: 'wrong'};

  const game = new Chess(fen);
  let played;
  try {
    played = game.move({from, to, promotion: promotion ?? expected.promotion ?? 'q'});
  } catch {
    return {status: 'wrong'};
  }
  if (!played) return {status: 'wrong'};

  const studentSan = played.san;
  const afterStudentFen = game.fen();

  const reply = expectedMoveForPly(moves, ply + 1);
  if (!reply) return {status: 'solved', fen: afterStudentFen, studentSan};

  const replyUci = parseUci(reply.move);
  let replySan = '';
  if (replyUci) {
    try {
      const replyPlayed = game.move({
        from: replyUci.from,
        to: replyUci.to,
        promotion: replyUci.promotion ?? 'q',
      });
      replySan = replyPlayed?.san ?? '';
    } catch {
      // A malformed scripted reply should not strand the student mid-puzzle.
      return {status: 'solved', fen: afterStudentFen, studentSan};
    }
  }
  return {
    status: 'reply',
    fen: game.fen(),
    afterStudentFen,
    replyMove: reply.move,
    studentSan,
    replySan,
    nextPly: ply + 2,
  };
}

export function nextChapterIndex(
  chapters: CourseChapter[],
  currentIndex: number,
): number | null {
  const next = currentIndex + 1;
  return next < (chapters?.length ?? 0) ? next : null;
}

export function isChapterComplete(
  chapter: CourseChapter,
  completedPageIds: Set<number>,
): boolean {
  const pages = chapter?.pages ?? [];
  return pages.length > 0 && pages.every(p => completedPageIds.has(p.id));
}
